import {ActionType} from "@/util/type/store.ts";
import {LoginResponse} from "../../model/response/authentication/LoginResponse.tsx";
import {CompleteEnrolementRequest} from "@/model/request/auth/CompleteEnrolementRequest.ts";
import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {ResetPasswordResponse} from "@/model/response/ResetPasswordResponse.ts";
import {ChangePasswordResponse} from "@/model/response/authentication/ChangePasswordResponse.ts";
import {SignUpResponse} from "@/model/response/SignUpResponse.ts";
import {CompleteEnrolementResponse} from "@/model/response/authentication/CompleteEnrolementResponse.ts";
import {ChangePasswordRequest} from "@/model/request/auth/ChangePasswordRequest.ts";
import {LoginRequest} from "@/model/request/auth/LoginRequest.ts";
import {SignUpRequest} from "@/model/request/auth/SignUpRequest.ts";
import {CompletePasswordReset} from "@/model/request/auth/CompletePasswordReset.ts";
import {ResetPassword} from "@/model/request/auth/ResetPassword.ts";
import {AuthService} from "@/service/AuthService.ts";
import {ResendOtpRequest} from "@/model/request/auth/ResendOtpRequest.ts";
import {BaseResponse} from "@/model/response/BaseResponse.ts";
import {application} from "@/store/modules/application.ts";
import {authConfig} from "@/store/modules/authConfig.ts";
import {generateCode} from "@/store/modules/generateCode.ts";
import {invite} from "@/store/modules/invite.ts";
import {modal} from "@/store/modules/modal.ts";
import {entity} from "@/store/modules/entity.ts";
import {attribute} from "@/store/modules/attribute.ts";
import {base} from "@/store/modules/base.ts";
import {deploy} from "@/store/modules/deploy.ts";
import {workspace} from "@/store/modules/workspace.ts";
import {user} from "@/store/modules/user.ts";
import {team} from "@/store/modules/team.ts";


export type AuthState = {
    token: string;
    loading: boolean;
    userInfo: LoginResponse;
    signInForm: SignUpRequest;
    userEmail: string
    userName: string
    tempEmail: string,
    otp: any
};

const initialState: AuthState = {
    userEmail: "",
    userName: "",
    tempEmail: "",
    otp: "",
    token: "",
    loading: false,
    userInfo: {} as LoginResponse,
    signInForm: {} as SignUpRequest
};

const action = {
    login: createAsyncThunk(
        "auth/login",
        async (payload: LoginRequest, thunkAPI) => {
            try {
                const response = await AuthService.login(thunkAPI, payload)
                return response.data
            } catch (error: any) {
                return thunkAPI.rejectWithValue(error.message)
            }
        }
    ),

    signup: createAsyncThunk(
        "auth/signup",
        async (payload: ActionType<SignUpRequest>, thunkAPI) => {
            return (await AuthService.signup(thunkAPI, payload.data)).data;
        }
    ),

    completeEnrollment: createAsyncThunk(
        "auth/completeEnrollment",
        async (payload: ActionType<CompleteEnrolementRequest>, thunkAPI) => {
            return (await AuthService.completeEnrollment(thunkAPI, payload.data)).data;
        }
    ),
    resendOtp: createAsyncThunk(
        "auth/resendOtp", async (payload: ActionType<ResendOtpRequest>, thunkAPI) => {
            return (await AuthService.resendOtp(thunkAPI, payload.data)).data;
        }
    ),

    // invite: createAsyncThunk(
    //     "auth/invite",
    //     async (payload: ActionType<CreateInviteRequest>, thunkAPI) => {
    //         return (await AuthService.invite(thunkAPI, payload.data)).data;
    //     }
    // ),
    reset: createAsyncThunk(
        "auth/reset",
        async (payload: ActionType<ResetPassword>, thunkAPI) => {
            return (await AuthService.reset(thunkAPI, payload.data)).data;
        }
    ),
    resetPassword: createAsyncThunk(
        "auth/reset-password",
        async (payload: ActionType<CompletePasswordReset>, thunkAPI) => {
            return (await AuthService.resetPassword(thunkAPI, payload?.data)).data;
        }
    ),
    changePassword: createAsyncThunk(
        "auth/change-password",
        async (payload: ActionType<ChangePasswordRequest>, thunkAPI) => {
            return (await AuthService.changePassword(thunkAPI, payload.data)).data;
        }
    ),
    logout: createAsyncThunk(
        "auth/logout",
        async (_, thunkAPI) => {
            try {
                thunkAPI.dispatch(application.mutation.reset)
                thunkAPI.dispatch(attribute.mutation.reset)
                thunkAPI.dispatch(authConfig.mutation.reset)
                thunkAPI.dispatch(base.mutation.resetModalOptions)
                thunkAPI.dispatch(deploy.mutation.reset)
                thunkAPI.dispatch(entity.mutation.reset)
                thunkAPI.dispatch(generateCode.mutation.reset)
                thunkAPI.dispatch(invite.mutation.reset)
                thunkAPI.dispatch(modal.mutation.reset)
                thunkAPI.dispatch(team.mutation.reset)
                thunkAPI.dispatch(user.mutation.reset)
                thunkAPI.dispatch(workspace.mutation.reset)
            } catch (e: any) {
                thunkAPI.rejectWithValue(e.message)
            }
        }
    ),
};

const slice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        setToken(state, {payload}) {
            state.token = payload;
        },
        setLoading(state, {payload}) {
            state.loading = payload;
        },
        setUserInfo(state, {payload}) {
            state.userInfo = payload;
        },
        setUserName(state, {payload}) {
            state.userName = payload;
        },
        setUserEmail(state, {payload}) {
            state.userEmail = payload;
        },
        setTempEmail(state, {payload}) {
            state.tempEmail = payload;
        },
        setOtp(state, {payload}) {
            state.otp = payload;
        },
        setCoreAuthState: (state, {payload}: {
            payload: LoginResponse
        }) => {
            state.token = payload.token;
            state.userInfo = payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(action.login.pending, (state) => {
                state.loading = true;
            })
            .addCase(action.login.fulfilled, (state, action) => {
                const response = action.payload as LoginResponse;
                state.loading = false
                state.token = response.token
                window.localStorage.token = response.token
                if (response.responseCode === "00") {
                    state.userInfo = response
                    // action.meta.arg.onSuccess?.(action.payload);
                } else {
                    // action.meta.arg.onError?.(action.payload);
                }
            })
            // .addCase(action.invite.pending, (state) => {
            //     state.loading = true;
            // })
            // .addCase(action.invite.fulfilled, (state, action) => {
            //     const response = action.payload as InviteResponse;
            //     state.loading = false;
            //     if (response.responseCode === "00")
            //         action.meta.arg.onSuccess?.(action.payload);
            //     else {
            //         action.meta.arg.onError?.(action.payload);
            //     }
            // })
            .addCase(action.signup.pending, (state) => {
                state.loading = true;
            })
            .addCase(action.signup.fulfilled, (state, action) => {
                const response = action.payload as SignUpResponse;
                state.loading = false;
                if (response.responseCode === "00") {
                    action.meta.arg.onSuccess?.(action.payload);
                } else {
                    action.meta.arg.onError?.(action.payload);
                }
            })
            .addCase(action.completeEnrollment.pending, (state) => {
                state.loading = true;
            })
            .addCase(action.completeEnrollment.fulfilled, (state, action) => {
                const response = action.payload as CompleteEnrolementResponse;
                if (response.responseCode === "00") {
                    state.loading = false;
                    action.meta.arg.onSuccess?.(action.payload);
                } else {
                    action.meta.arg.onError?.(action.payload);
                }
            })
            .addCase(action.reset.pending, (state) => {
                state.loading = true;
            })
            .addCase(action.reset.fulfilled, (state, action) => {
                const response = action.payload as ResetPasswordResponse;
                state.loading = false;
                if (response.responseCode === "00")
                    action.meta.arg.onSuccess?.(action.payload);
                else {
                    action.meta.arg.onError?.(action.payload);
                }
            })
            .addCase(action.resetPassword.pending, (state) => {
                state.loading = true;
            })
            .addCase(action.resetPassword.fulfilled, (state, action) => {
                const response = action.payload as ResetPasswordResponse;
                state.loading = false;
                if (response.responseCode === "00")
                    action.meta.arg.onSuccess?.(action.payload);
                else {
                    action.meta.arg.onError?.(action.payload);
                }
            })
            .addCase(action.changePassword.pending, (state) => {
                state.loading = true;
            })
            .addCase(action.changePassword.fulfilled, (state, action) => {
                const response = action.payload as ChangePasswordResponse;
                state.loading = false;
                if (response.responseCode === "00")
                    action.meta.arg.onSuccess?.(action.payload);
                else {
                    action.meta.arg.onError?.(action.payload);
                }
            }).addCase(action.resendOtp.pending, (state) => {
            state.loading = false
        }).addCase(action.resendOtp.fulfilled, (state, action) => {
            const response = action.payload as BaseResponse
            if (response.responseCode == "00") {
                action.meta.arg.onSuccess?.(action.payload)
            }
        });
    },
});

export const auth = {
    reducer: slice.reducer,
    action: action,
    mutation: slice.actions,
};
